// src/pages/LotteryWheel.js
import React, { useEffect, useMemo, useState, useRef } from 'react';
import pageBack from '../assets/card_back.png';

// 這裡填你的 Google Sheet 試算表（同一份你給我的那個）
// ✅ 設定方式：
// 1. 到 Google Sheet → 右上角 [分享] → 設成「擁有連結的任何人都可以檢視」
// 2. 試算表網址長得像：
//    https://docs.google.com/spreadsheets/d/【這段是 SHEET_ID】/edit?gid=0#gid=0
// 3. 我已經幫你把你提供的那份 SHEET_ID 和 gid=0 寫在下面，會用 CSV 方式抓資料
const SHEET_ID = '15jG7cUdn1caMBPdjpIbiqJHdO_3AaL3j-fN94pLhB40';
const SHEET_GID = '0'; // 如果以後換工作表分頁，這個 gid 可能會變

// Google Sheet 匯出 CSV 的固定格式
const SHEET_CSV_URL = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/export?format=csv&gid=${SHEET_GID}`;

/**
 * 抽獎轉盤頁面
 *
 * 使用方式：
 * - 從 Google Sheet 複製整欄名單（每人一列），貼到左側文字框
 * - 輸入抽獎內容（例：兩張日券）與本次名額（例：5）
 * - 按「開始抽獎」→ 一次抽一個，轉盤上顯示名字，已抽中的反灰
 */
export default function LotteryWheel() {
  const [prize, setPrize] = useState('兩張日券');
  const [winnerCount, setWinnerCount] = useState('5');
  const [rawNames, setRawNames] = useState('');

  const [pool, setPool] = useState([]); // 目前可抽的人
  const [drawnNames, setDrawnNames] = useState([]); // 已抽中的人（用於反灰）
  const [error, setError] = useState('');
  const [results, setResults] = useState([]); // 最近一次中獎結果
  const [showResultModal, setShowResultModal] = useState(false);

  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [currentDrawQueue, setCurrentDrawQueue] = useState([]); // 本次要抽的名單
  const [currentDrawIndex, setCurrentDrawIndex] = useState(0); // 目前抽到第幾個
  
  // 使用 ref 來確保能拿到最新的狀態值
  const drawQueueRef = useRef([]);
  const drawIndexRef = useRef(0);
  const poolRef = useRef([]);
  const drawnNamesRef = useRef([]);
  const rotationRef = useRef(0);

  // 輪盤顏色（咖啡色和米色系，參考會員卡系統色調）
  const COLOR_SET = ['#8B6F47', '#A0826D', '#C4A484', '#D4C4B0', '#E8DCC6', '#F5E6D3', '#6B5B4B', '#5E3A1A', '#8B6F47', '#A0826D', '#C4A484', '#D4C4B0', '#E8DCC6', '#F5E6D3', '#6B5B4B', '#5E3A1A', '#8B6F47', '#A0826D', '#C4A484', '#D4C4B0', '#E8DCC6', '#F5E6D3', '#6B5B4B', '#5E3A1A'];

  // 解析名單為不重複陣列
  const parsedNames = useMemo(() => {
    const lines = String(rawNames || '')
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    return Array.from(new Set(lines));
  }, [rawNames]);

  const displayPool = pool.length ? pool : parsedNames;

  const poolInfoText = displayPool.length
    ? `目前抽獎池共有 ${displayPool.length} 人。中獎者會從池中移除。`
    : '';

  // 動態計算轉盤格數：根據實際參加人數，最多24格，最少8格
  const WHEEL_SECTIONS = useMemo(() => {
    const allNames = [...displayPool, ...drawnNames];
    const uniqueNames = Array.from(new Set(allNames));
    const count = uniqueNames.length;
    if (count === 0) return 24; // 沒人時預設24格
    if (count <= 8) return 8; // 最少8格
    if (count <= 24) return count; // 8-24人之間用人數
    return 24; // 超過24人最多24格
  }, [displayPool, drawnNames]);

  // 轉盤上要顯示的名字（根據實際格數）
  const wheelNames = useMemo(() => {
    const allNames = [...displayPool, ...drawnNames];
    const uniqueNames = Array.from(new Set(allNames));
    const names = uniqueNames.slice(0, WHEEL_SECTIONS);
    // 補齊到格數
    while (names.length < WHEEL_SECTIONS) {
      names.push('');
    }
    return names;
  }, [displayPool, drawnNames, WHEEL_SECTIONS]);

  // 產生動態格數的輪盤背景（conic-gradient），已抽中的反灰
  const wheelBackground = useMemo(() => {
    const sector = 360 / WHEEL_SECTIONS;
    const parts = [];
    for (let i = 0; i < WHEEL_SECTIONS; i++) {
      const name = wheelNames[i];
      const isDrawn = name && drawnNames.includes(name);
      // 已抽中的用灰色，未抽中的用原色
      const color = isDrawn ? '#CCCCCC' : COLOR_SET[i % COLOR_SET.length];
      const start = i * sector;
      const end = (i + 1) * sector;
      parts.push(`${color} ${start}deg ${end}deg`);
    }
    return `conic-gradient(${parts.join(',')})`;
  }, [wheelNames, drawnNames, WHEEL_SECTIONS]);

  // 進頁面時，自動從 Google Sheet 抓名單
  useEffect(() => {
    let cancelled = false;

    const loadFromSheet = async () => {
      try {
        const res = await fetch(SHEET_CSV_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();

        // 簡單把 CSV 的每一行第一欄當作名字
        const lines = text
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        if (!lines.length) {
          return;
        }

        // 如果第一列是標題，例如「姓名」之類，嘗試略過
        let nameLines = lines;
        const first = lines[0];
        if (/姓名|name|Name|名稱|member/i.test(first)) {
          nameLines = lines.slice(1);
        }

        if (!cancelled) {
          const joined = nameLines.join('\n');
          setRawNames(joined);
          setPool([]); // 讓邏輯以 rawNames 重新生成池子
          setDrawnNames([]); // 重置已抽中名單
        }
      } catch (e) {
        // 靜默失敗
      }
    };

    loadFromSheet();

    return () => {
      cancelled = true;
    };
  }, []);

  const buildPoolIfNeeded = () => {
    if (pool.length) {
      poolRef.current = pool;
      return pool;
    }
    if (!parsedNames.length) {
      poolRef.current = [];
      return [];
    }
    setPool(parsedNames);
    poolRef.current = parsedNames;
    return parsedNames;
  };
  
  // 當 pool 或 drawnNames 改變時，同步更新 ref
  useEffect(() => {
    poolRef.current = pool;
  }, [pool]);
  
  useEffect(() => {
    drawnNamesRef.current = drawnNames;
  }, [drawnNames]);
  
  useEffect(() => {
    rotationRef.current = rotation;
  }, [rotation]);

  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // 根據轉盤旋轉角度計算指針指向的扇形索引
  const getPointerIndex = (rotation, sections) => {
    // 指針固定在頂部（-90度或270度位置，正上方）
    // 在CSS transform中，0度是右邊（3點鐘），90度是下方（6點鐘），180度是左邊（9點鐘），270度是上方（12點鐘）
    // 指針在視覺上的正上方，對應的是 -90度或270度
    const normalizedRotation = ((rotation % 360) + 360) % 360; // 正規化到 0-360
    const sectorAngle = 360 / sections;
    const pointerAngle = 270; // 指針在270度位置（正上方，-90度）
    
    // 計算：哪個標籤的實際角度在270度附近
    // 標籤 idx 的初始角度是 sector * idx + sector / 2（標籤在扇形中心）
    // 轉盤轉了 rotation 度後，標籤 idx 的實際角度是 (sector * idx + sector / 2 + rotation) mod 360
    // 指針在270度，所以指針指向的是「實際角度為 270 度的標籤」
    let minDiff = Infinity;
    let targetIndex = 0;
    for (let i = 0; i < sections; i++) {
      const labelAngle = (i * sectorAngle + sectorAngle / 2 + normalizedRotation) % 360;
      // 計算標籤角度與指針角度（270度）的差值
      let diff = Math.abs(labelAngle - pointerAngle);
      if (diff > 180) {
        diff = 360 - diff; // 取較小的角度差
      }
      if (diff < minDiff) {
        minDiff = diff;
        targetIndex = i;
      }
    }
    return targetIndex;
  };

  // 執行單次抽獎
  const executeSingleDraw = () => {
    // 使用 ref 來確保拿到最新值
    const queue = drawQueueRef.current;
    const index = drawIndexRef.current;
    const currentPool = poolRef.current;
    const currentDrawn = drawnNamesRef.current;

    // 檢查是否全部抽完
    if (index >= queue.length) {
      // 全部抽完，才一次性設定 results 並顯示彈窗
      setResults(
        queue.map((name, idx) => ({
          name,
          index: idx + 1,
        })),
      );
      setSpinning(false);
      setShowResultModal(true);
      setCurrentDrawQueue([]);
      setCurrentDrawIndex(0);
      drawQueueRef.current = [];
      drawIndexRef.current = 0;
      return;
    }

    // 獲取當前可抽的名單（排除已抽中的）
    const availableNames = wheelNames.filter((name) => name && !currentDrawn.includes(name));
    if (availableNames.length === 0) {
      // 沒有可抽的人了
      setSpinning(false);
      return;
    }

    // 完全隨機轉動轉盤，不預先選擇中獎者
    // 轉盤隨機轉動：多轉幾圈 + 隨機角度
    const extraTurns = 3 + Math.floor(Math.random() * 3); // 3~5 圈
    const randomAngle = Math.random() * 360; // 隨機角度 0-360度
    setRotation((prev) => {
      const finalRotation = prev + extraTurns * 360 + randomAngle;
      // 保存最終旋轉角度到 ref，以便在 setTimeout 中使用
      rotationRef.current = finalRotation;
      return finalRotation;
    });

    // 等待轉盤動畫完成後，根據指針指向的位置確定中獎者
    setTimeout(() => {
      // 根據最終旋轉角度計算指針指向的扇形
      const finalRotation = rotationRef.current;
      const normalizedRotation = ((finalRotation % 360) + 360) % 360;
      const pointerIndex = getPointerIndex(finalRotation, WHEEL_SECTIONS);
      const winner = wheelNames[pointerIndex];

      // 使用指針指向的人作為中獎者（完全公平，沒有任何後台指定）
      let actualWinner = winner;
      
      // 如果指向的是空位或已抽中的人，重新轉動轉盤
      if (!actualWinner || currentDrawn.includes(actualWinner)) {
        console.log('指針指向的位置不可用，重新轉動:', {
          pointerIndex,
          winner,
          isDrawn: currentDrawn.includes(winner),
        });
        // 重新轉動轉盤
        setTimeout(() => {
          executeSingleDraw();
        }, 200); // 短暫延遲後重新轉動
        return;
      }
      
      // 只更新 drawnNames（用於反灰），不更新 pool（保持所有人在轉盤上）
      const newDrawnNames = [...currentDrawn, actualWinner];

      // 更新 ref
      drawnNamesRef.current = newDrawnNames;
      // 不更新 poolRef，保持所有人在轉盤上

      // 更新 state（只更新 drawnNames，不更新 pool）
      setDrawnNames(newDrawnNames);
      // 不更新 pool，保持所有人在轉盤上

      // 更新 queue（用於最後顯示結果）
      if (index < queue.length) {
        queue[index] = actualWinner;
      }
      
      // 調試信息：確認指針指向的位置
      console.log('轉盤停止:', {
        finalRotation: normalizedRotation,
        pointerIndex,
        winner,
        actualWinner,
        isAvailable: !currentDrawn.includes(actualWinner),
      });

      // 繼續抽下一個
      const nextIndex = index + 1;
      drawIndexRef.current = nextIndex;
      setCurrentDrawIndex(nextIndex);

      // 如果還有下一個，繼續抽；否則結束
      if (nextIndex < queue.length) {
        setTimeout(() => {
          executeSingleDraw();
        }, 1000); // 停1秒後抽下一個
      } else {
        // 全部抽完
        setResults(
          queue.map((name, idx) => ({
            name,
            index: idx + 1,
          })),
        );
        setSpinning(false);
        setShowResultModal(true);
        setCurrentDrawQueue([]);
        setCurrentDrawIndex(0);
        drawQueueRef.current = [];
        drawIndexRef.current = 0;
      }
    }, 1500); // 等待轉盤動畫完成（1.5秒）
  };

  const handleDraw = () => {
    if (spinning) return;

    setError('');

    if (!rawNames.trim()) {
      setError('請先貼上名單（每行一位）。');
      return;
    }

    let currentPool = buildPoolIfNeeded();
    if (!currentPool.length) {
      setError('名單為空，請確認輸入內容。');
      return;
    }

    const prizeText = (prize || '').trim() || '獎品';
    const count = Number.parseInt(winnerCount, 10) || 1;

    if (count <= 0) {
      setError('名額必須大於 0。');
      return;
    }
    if (count > currentPool.length) {
      setError(`目前抽獎池只有 ${currentPool.length} 人，無法抽出 ${count} 名。`);
      return;
    }

    // 準備要抽的名單
    const shuffled = shuffle(currentPool);
    const winners = shuffled.slice(0, count);

    // 更新 ref
    drawQueueRef.current = winners;
    drawIndexRef.current = 0;
    poolRef.current = currentPool;
    drawnNamesRef.current = drawnNames;

    setSpinning(true);
    setShowResultModal(false);
    setResults([]);
    setCurrentDrawQueue(winners);
    setCurrentDrawIndex(0);

    // 開始第一次抽獎
    setTimeout(() => {
      executeSingleDraw();
    }, 100);
  };

  // 移除這個 useEffect，轉盤轉動改在 executeSingleDraw 中處理

  return (
    <div
      style={{
        fontFamily: `system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
        backgroundImage: `url(${pageBack})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        margin: 0,
        padding: '120px 12px 24px 12px',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {/* 內嵌樣式（只對本頁生效） */}
      <style>
        {`
        .lottery-shell {
          max-width: 1200px;
          width: 100%;
          display: grid;
          grid-template-columns: minmax(0, 3fr) minmax(0, 5fr);
          gap: 18px;
          align-items: stretch;
        }
        /* 左邊：控制面板（給工作人員用） */
        .lottery-panel {
          background: linear-gradient(135deg, #F5E6D3, #E8DCC6);
          border-radius: 18px;
          padding: 18px 18px 18px;
          box-shadow: 0 18px 45px rgba(91, 58, 26, 0.3);
          border: 2px solid #D4C4B0;
          color: #5E3A1A;
        }
        .lottery-title {
          margin: 0 0 6px;
          font-size: 22px;
          font-weight: 800;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: #5E3A1A;
        }
        .lottery-subtitle {
          margin-bottom: 16px;
          color: #6B5B4B;
          font-size: 13px;
        }
        .lottery-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: #6B5B4B;
          margin: 8px 0 4px;
        }
        .lottery-input,
        .lottery-textarea {
          width: 100%;
          border-radius: 10px;
          border: 2px solid #C4A484;
          padding: 8px 10px;
          font-size: 13px;
          box-sizing: border-box;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
          background: #FFFFFF;
          color: #5E3A1A;
        }
        .lottery-input:focus,
        .lottery-textarea:focus {
          border-color: #8B6F47;
          box-shadow: 0 0 0 2px rgba(139, 111, 71, 0.3);
        }
        .lottery-textarea {
          min-height: 220px;
          line-height: 1.5;
          resize: vertical;
          font-family: inherit;
        }
        .lottery-small {
          color: #8B6F47;
          font-size: 11px;
        }
        .lottery-row {
          display: flex;
          gap: 10px;
          margin-bottom: 4px;
        }
        .lottery-row > div {
          flex: 1;
        }
        .lottery-button {
          margin-top: 10px;
          border: none;
          padding: 10px 20px;
          border-radius: 999px;
          background: linear-gradient(135deg, #8B6F47, #6B5B4B);
          color: #F5E6D3;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          box-shadow: 0 10px 30px rgba(91, 58, 26, 0.4);
          transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease;
        }
        .lottery-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          box-shadow: none;
          filter: grayscale(0.2);
        }
        .lottery-button:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow: 0 16px 40px rgba(91, 58, 26, 0.6);
          filter: brightness(1.1);
        }
        .lottery-error {
          color: #C85A5A;
          font-size: 12px;
          margin-top: 4px;
          min-height: 16px;
        }
        .lottery-pool-info {
          font-size: 11px;
          color: #6B5B4B;
          margin-top: 4px;
        }

        /* 右邊：客人看到的畫面 */
        .lottery-guest {
          position: relative;
          border-radius: 24px;
          overflow: visible;
          background: transparent;
        }
        .lottery-guest-bg {
          display: none;
        }
        .lottery-guest-overlay {
          position: relative;
          z-index: 1;
          inset: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px;
          box-sizing: border-box;
          min-height: 100%;
        }
        .lottery-wheel-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
        }
        .lottery-wheel-container {
          position: relative;
          width: min(480px, 80vw);
          aspect-ratio: 1 / 1;
        }
        .lottery-wheel {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          border: 12px solid #6B5B4B;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          box-shadow: 0 24px 70px rgba(91, 58, 26, 0.6);
          transition: transform 1.5s cubic-bezier(0.25, 0.46, 0.45, 1);
        }
        .lottery-wheel-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 120px;
          height: 120px;
          background: radial-gradient(circle at 30% 20%, #F5E6D3, #D4C4B0);
          border-radius: 50%;
          border: 4px solid #8B6F47;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 900;
          font-size: 18px;
          color: #5E3A1A;
          letter-spacing: 0.18em;
          box-shadow: 0 16px 40px rgba(91, 58, 26, 0.6);
          z-index: 10;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .lottery-wheel-center:hover {
          transform: translate(-50%, -50%) scale(1.05);
          box-shadow: 0 20px 50px rgba(91, 58, 26, 0.8);
        }
        .lottery-wheel-center:active {
          transform: translate(-50%, -50%) scale(0.98);
        }
        .lottery-wheel-labels {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          color: #111827;
          text-align: center;
          pointer-events: none;
          font-weight: 800;
        }
        .lottery-wheel-labels span {
          position: absolute;
          width: 45%;
          left: 50%;
          transform-origin: 0% 50%;
          white-space: nowrap;
          text-shadow: 0 1px 3px rgba(255,255,255,0.9), 0 0 2px rgba(91, 58, 26, 0.3);
          color: #5E3A1A;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .lottery-wheel-labels span.drawn {
          color: #999999;
          opacity: 0.5;
        }
        .lottery-pointer {
          position: absolute;
          top: -4px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 14px solid transparent;
          border-right: 14px solid transparent;
          border-top: 28px solid #8B6F47;
          filter: drop-shadow(0 4px 6px rgba(91, 58, 26, 0.7));
          z-index: 5;
        }

        /* 中獎名單彈窗 */
        .lottery-modal {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.75);
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          box-sizing: border-box;
          animation: lottery-modal-fade-in 0.3s ease-out;
        }
        .lottery-modal-content {
          background: linear-gradient(135deg, #F5E6D3, #E8DCC6);
          border-radius: 20px;
          padding: 20px 16px;
          max-width: 460px;
          width: 100%;
          max-height: 80vh;
          box-shadow: 0 24px 60px rgba(91, 58, 26, 0.6);
          border: 3px solid #8B6F47;
          animation: lottery-modal-scale-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          display: flex;
          flex-direction: column;
        }
        .lottery-modal-title {
          margin: 0 0 16px;
          font-size: 24px;
          font-weight: 900;
          color: #5E3A1A;
          text-align: center;
          letter-spacing: 0.1em;
        }
        .lottery-modal-prize {
          text-align: center;
          color: #6B5B4B;
          font-size: 14px;
          margin-bottom: 20px;
        }
        .lottery-modal-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
          overflow-y: auto;
          flex: 1;
          min-height: 0;
        }
        .lottery-modal-item {
          background: #FFFFFF;
          border-radius: 10px;
          padding: 12px 10px;
          border: 2px solid #D4C4B0;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .lottery-modal-item-name {
          font-size: 18px;
          font-weight: 800;
          color: #5E3A1A;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .lottery-modal-item-badge {
          display: inline-flex;
          padding: 5px 10px;
          border-radius: 999px;
          font-size: 14px;
          font-weight: 700;
          background: linear-gradient(135deg, #8B6F47, #6B5B4B);
          color: #F5E6D3;
          box-shadow: 0 4px 12px rgba(91, 58, 26, 0.4);
          white-space: nowrap;
          min-width: 36px;
          justify-content: center;
        }
        .lottery-modal-close {
          margin-top: 20px;
          width: 100%;
          padding: 12px;
          border: none;
          border-radius: 10px;
          background: linear-gradient(135deg, #8B6F47, #6B5B4B);
          color: #F5E6D3;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: transform 0.12s ease, box-shadow 0.12s ease;
        }
        .lottery-modal-close:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(91, 58, 26, 0.5);
        }
        @keyframes lottery-modal-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes lottery-modal-scale-in {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @media (max-width: 960px) {
          .lottery-shell {
            grid-template-columns: 1fr;
            display: flex;
            flex-direction: column;
          }
          .lottery-guest {
            margin-top: 18px;
            order: 1;
          }
          .lottery-panel {
            order: 2;
          }
          .lottery-guest-overlay {
            padding: 16px;
          }
          .lottery-wheel-container {
            width: min(360px, 90vw);
          }
          .lottery-modal-content {
            padding: 24px;
          }
          .lottery-modal-title {
            font-size: 24px;
          }
          .lottery-modal-item-name {
            font-size: 18px;
          }
        }
      `}
      </style>

      <div className="lottery-shell">
        {/* 左側：設定區（工作人員操作） */}
        <div className="lottery-panel">
          <h1 className="lottery-title">抽獎轉盤</h1>
          <div className="lottery-subtitle" />

          <div className="lottery-row">
            <div>
              <label className="lottery-label">抽獎內容（獎品說明）</label>
              <input
                className="lottery-input"
                type="text"
                value={prize}
                onChange={(e) => setPrize(e.target.value)}
                placeholder="例：兩張日券 / 800 元禮券 / 特別獎"
              />
            </div>
            <div style={{ maxWidth: 140 }}>
              <label className="lottery-label">名額（一次抽出幾人）</label>
              <input
                className="lottery-input"
                type="number"
                min="1"
                value={winnerCount}
                onChange={(e) => setWinnerCount(e.target.value)}
              />
            </div>
          </div>

          <label className="lottery-label">名單（每行一位，可從 Google Sheet 複製貼上）</label>
          <textarea
            className="lottery-textarea"
            value={rawNames}
            onChange={(e) => {
              setRawNames(e.target.value);
              setPool([]); // 名單改變時，讓池子以新名單為準
              setDrawnNames([]); // 重置已抽中名單
            }}
            placeholder={'例：\n王小明\n陳大文\n林小美\n...'}
          />
          <div className="lottery-small">
            小提示：在 Google Sheet 中選整欄的名字，複製後直接貼到這裡即可。
          </div>

          <div className="lottery-error">{error}</div>
          <button className="lottery-button" onClick={handleDraw} disabled={spinning}>
            <span role="img" aria-label="wheel">
              🎡
            </span>
            <span>{spinning ? '轉盤旋轉中…' : '開始抽獎'}</span>
          </button>
          {poolInfoText && <div className="lottery-pool-info">{poolInfoText}</div>}
        </div>

        {/* 右側：客人畫面（大螢幕展示，24格輪盤） */}
        <div className="lottery-guest">
          <div
            className="lottery-guest-bg"
            style={{ backgroundImage: `url(${pageBack})` }}
          />
          <div className="lottery-guest-overlay">
            <div className="lottery-wheel-wrapper">
              <div className="lottery-wheel-container">
                <div className="lottery-pointer" />
                <div
                  className="lottery-wheel"
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    backgroundImage: wheelBackground,
                  }}
                >
                  <div className="lottery-wheel-labels">
                    {wheelNames.map((name, idx) => {
                      const sector = 360 / WHEEL_SECTIONS;
                      const angle = sector * idx + sector / 2; // 偏移半個扇形角度，讓名字在扇形中間
                      const isDrawn = name && drawnNames.includes(name);
                      return (
                        <span
                          key={idx}
                          className={isDrawn ? 'drawn' : ''}
                          style={{
                            transform: `rotate(${angle}deg) translateX(26%)`,
                          }}
                          title={name || ''}
                        >
                          {name || ''}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <div className="lottery-wheel-center" onClick={handleDraw} style={{ pointerEvents: spinning ? 'none' : 'auto' }}>
                  齊文藝室
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 中獎名單彈窗 */}
      {showResultModal && (
        <div className="lottery-modal" onClick={() => setShowResultModal(false)}>
          <div className="lottery-modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="lottery-modal-title">齊文藝室 🎉 中獎名單 Winners</h2>
            <div className="lottery-modal-prize">
              本次抽出 {results.length} 名，獎品：<b>{(prize || '').trim() || '獎品'}</b>
            </div>
            <div className="lottery-modal-list">
              {results.map((r) => (
                <div key={r.index + r.name} className="lottery-modal-item">
                  <span className="lottery-modal-item-badge">
                    {r.index}
                  </span>
                  <span className="lottery-modal-item-name">{r.name}</span>
                </div>
              ))}
            </div>
            <button className="lottery-modal-close" onClick={() => setShowResultModal(false)}>
              關閉
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
